import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';

import { ContentV1 } from '@jebel/constants';
import { generateContentMediaSourceSet, transformContentMedia } from '@jebel/utils';

/**
 * Hook to upload media files to the Content API.
 */
export function useMediaUploader() {
  return useMutation({
    retry: 2,
    mutationFn: async (file: File) => {
      const form = new FormData();

      form.append('file', file);

      const url = new URL('/media/upload', process.env.REACT_APP_CONTENT_API_URL);

      const response = await fetch(url.toString(), {
        method: 'POST',
        body: form,
      });

      const data = await response.json();
      return data as ContentV1.Media;
    },
  });
}

/**
 * Hook to dispose (delete or mark as deleted) a media file by its ID.
 * Uses the Content API `/media/:id/dispose` endpoint.
 */
export function useMediaDisposer() {
  return useMutation({
    retry: 2,

    mutationFn: async (id: string) => {
      const url = new URL(`/media/${id}/dispose`, process.env.REACT_APP_CONTENT_API_URL);

      const response = await fetch(url.toString(), {
        method: 'POST',
      });

      return response.ok;
    },
  });
}

const MEDIA_DETAILER_QUERY_KEY = 'MEDIA_DETAILER';

/**
 * Hook to fetch the details of a media file by its ID.
 * @param {string} id - The ID of the media file to fetch.
 */
export function useMediaDetailer(id: string | undefined | null) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: [MEDIA_DETAILER_QUERY_KEY, id],

    queryFn: async () => {
      const url = new URL(`/media/${id}`, process.env.REACT_APP_CONTENT_API_URL);
      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`Failed to fetch media detail (HTTP ${response.status})`);
      }

      const data = await response.json();
      return data as ContentV1.Media;
    },
  });
}

/**
 * Hook to generate image preview sources for a media file by its ID.
 *
 * @param {string} id - The ID of the media file to generate preview URLs for.
 */
export function useMediaPreview(id: string | undefined | null) {
  const src = useMemo(() => {
    if (!id) {
      return undefined;
    }

    return transformContentMedia(process.env.REACT_APP_CONTENT_API_URL!, id, {
      scale: 0.5,
    });
  }, [id]);

  const srcSet = useMemo(() => {
    if (!id) {
      return undefined;
    }

    return generateContentMediaSourceSet(process.env.REACT_APP_CONTENT_API_URL!, id);
  }, [id]);

  return {
    src,
    srcSet,
  };
}
